import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
// @ts-expect-error - the browser bundle ships without its own TypeScript declarations.
import SockJS from 'sockjs-client/dist/sockjs';
import { API_BASE_URL } from '../config/env';
import { getAccessToken } from './storage/authTokens';
import { getAdminAuthUser } from './storage/adminFlow';

export interface AdminConversationSocketHandlers {
  onMessage: (payload: Record<string, unknown> | null) => void;
  onConnectionChange?: (connected: boolean) => void;
}

function messagingBaseUrl(): string {
  const base = String(API_BASE_URL ?? '').trim();
  if (!base) throw new Error('Missing API base URL for WebSocket construction');

  const parsed = new URL(base.replace(/^ws(s)?:/i, (match) => (/^wss/i.test(match) ? 'https:' : 'http:')));
  const path = parsed.pathname.replace(/\/+$/, '');
  const messagingPath = path.endsWith('/messaging-service') ? path : `${path}/messaging-service`;

  return `${parsed.origin}${messagingPath}`;
}

function sockJsUrl(): string {
  const bearer = getAccessToken('admin') ?? '';
  const token = bearer.startsWith('Bearer ') ? bearer.slice(7) : bearer;

  return `${messagingBaseUrl()}/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
}

function connectHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const adminUser = getAdminAuthUser();
  const bearer = getAccessToken('admin');

  if (adminUser?.id != null) headers['X-Principal-Id'] = String(adminUser.id);
  headers['X-Principal-Type'] = 'ADMIN';
  if (adminUser?.roles?.length) headers['X-Principal-Roles'] = adminUser.roles.join(',');
  if (bearer) headers.Authorization = bearer.startsWith('Bearer ') ? bearer : `Bearer ${bearer}`;

  return headers;
}

function parseBody(message: IMessage): Record<string, unknown> | null {
  if (!message.body) return null;

  try {
    return JSON.parse(message.body) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function subscribeToAdminConversation(
  conversationId: string,
  handlers: AdminConversationSocketHandlers,
): () => void {
  let subscription: StompSubscription | null = null;
  let disposed = false;

  const client = new Client({
    webSocketFactory: () => new SockJS(sockJsUrl()) as unknown as WebSocket,
    connectHeaders: connectHeaders(),
    heartbeatIncoming: 20000,
    heartbeatOutgoing: 20000,
    reconnectDelay: 5000,
    connectionTimeout: 15000,
    onConnect: () => {
      if (disposed) return;

      subscription = client.subscribe(`/topic/conversations.${conversationId}`, (message) => {
        handlers.onMessage(parseBody(message));
      });

      handlers.onConnectionChange?.(true);
    },
    onWebSocketClose: () => handlers.onConnectionChange?.(false),
    onStompError: () => handlers.onConnectionChange?.(false),
  });

  client.activate();

  return () => {
    disposed = true;
    try {
      subscription?.unsubscribe();
    } catch {
      // The socket may already be gone.
    }
    subscription = null;
    void client.deactivate();
    handlers.onConnectionChange?.(false);
  };
}
